(() => {
  const mobileProcessQuery = window.matchMedia('(max-width: 749px)');
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  let activeDialogTrigger = null;

  const openDialog = (dialog, trigger) => {
    if (!dialog) return;
    activeDialogTrigger = trigger;
    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
    document.documentElement.classList.add('mtm-lightbox-open');
  };

  const closeDialog = (dialog) => {
    if (!dialog) return;
    if (typeof dialog.close === 'function') {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
      document.documentElement.classList.remove('mtm-lightbox-open');
      activeDialogTrigger?.focus();
      activeDialogTrigger = null;
    }
  };

  const initializeLightboxes = (scope) => {
    scope.querySelectorAll('.mtm-lightbox:not([data-mtm-initialized])').forEach((dialog) => {
      dialog.dataset.mtmInitialized = 'true';

      dialog.querySelectorAll('[data-lightbox-close]').forEach((button) => {
        button.addEventListener('click', () => closeDialog(dialog));
      });

      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) closeDialog(dialog);
      });

      dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('mtm-lightbox-open');
        activeDialogTrigger?.focus();
        activeDialogTrigger = null;
      });
    });

    scope.querySelectorAll('[data-project-lightbox-trigger]:not([data-mtm-initialized])').forEach((trigger) => {
      trigger.dataset.mtmInitialized = 'true';
      trigger.addEventListener('click', () => {
        const gallery = trigger.closest('.mtm-photo-gallery');
        const card = trigger.closest('.mtm-photo-card');
        const dialog = gallery?.querySelector('[data-project-lightbox]');
        const sourceImage = trigger.querySelector('img');
        if (!dialog || !sourceImage) return;

        const dialogImage = dialog.querySelector('[data-lightbox-image]');
        const dialogTitle = dialog.querySelector('[data-lightbox-title]');
        const dialogCaption = dialog.querySelector('[data-lightbox-caption]');
        const title = card?.querySelector('h3')?.textContent?.trim() || sourceImage.alt;
        const caption = card?.querySelector('figcaption p')?.textContent?.trim() || '';

        dialogImage.src = trigger.dataset.projectFullSrc || sourceImage.currentSrc || sourceImage.src;
        dialogImage.alt = sourceImage.alt;
        dialogTitle.textContent = title;
        dialogCaption.textContent = caption;
        dialogCaption.hidden = !caption;
        openDialog(dialog, trigger);
      });
    });

    scope.querySelectorAll('[data-door-lightbox-trigger]:not([data-mtm-initialized])').forEach((trigger) => {
      trigger.dataset.mtmInitialized = 'true';
      trigger.addEventListener('click', () => {
        const dialog = document.querySelector('[data-door-lightbox]');
        const sourceImage = trigger.querySelector('img');
        if (!dialog || !sourceImage) return;

        const dialogImage = dialog.querySelector('[data-lightbox-image]');
        const sample = {src: sourceImage.currentSrc || sourceImage.src, alt: sourceImage.alt};
        const kitchen = trigger.dataset.doorKitchenSrc;
        const images = kitchen ? [sample, {src: kitchen, alt: `${trigger.dataset.doorName} kitchen`}] : [sample];
        if (trigger.dataset.doorKitchenSecondSrc) images.push({src: trigger.dataset.doorKitchenSecondSrc, alt: `${trigger.dataset.doorName} kitchen`});
        const thumbs = dialog.querySelector('[data-door-gallery-thumbs]');
        const buttons = [...thumbs.querySelectorAll('button')];
        const select = (index) => {
          dialogImage.src = images[index].src;
          dialogImage.alt = images[index].alt;
          buttons.forEach((button, i) => button.setAttribute('aria-pressed', String(i === index)));
        };
        thumbs.hidden = images.length < 2;
        buttons.forEach((button, index) => {
          button.hidden = !images[index];
          button.querySelector('img').src = images[index]?.src || sample.src;
          button.onclick = () => select(index);
        });
        dialog.onkeydown = (event) => {
          if (images.length < 2 || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
          event.preventDefault();
          const index = buttons.findIndex(button => button.getAttribute('aria-pressed') === 'true');
          select((index + (event.key === 'ArrowLeft' ? -1 : 1) + images.length) % images.length);
        };
        select(0);
        dialog.querySelector('[data-lightbox-title]').textContent = trigger.dataset.doorName || sourceImage.alt;
        dialog.querySelector('[data-lightbox-code]').textContent = trigger.dataset.doorCode || '';
        dialog.querySelector('[data-lightbox-eyebrow]').textContent = trigger.dataset.doorSeries || '';
        openDialog(dialog, trigger);
      });
    });
  };

  const initializeProjectFilters = (scope) => {
    scope.querySelectorAll('[data-project-filters]:not([data-mtm-initialized])').forEach((filterGroup) => {
      filterGroup.dataset.mtmInitialized = 'true';
      const gallery = filterGroup.closest('.mtm-photo-gallery');
      const cards = gallery?.querySelectorAll('[data-project-category]') || [];
      const buttons = filterGroup.querySelectorAll('[data-project-filter]');

      buttons.forEach((button) => {
        button.addEventListener('click', () => {
          const category = button.dataset.projectFilter;
          buttons.forEach((item) => {
            const selected = item === button;
            item.classList.toggle('is-active', selected);
            item.setAttribute('aria-pressed', selected ? 'true' : 'false');
          });
          cards.forEach((card) => {
            card.hidden = category !== 'all' && card.dataset.projectCategory !== category;
          });
        });
      });
    });
  };

  const initializeSeriesNavigation = (scope) => {
    scope.querySelectorAll('[data-series-nav]:not([data-mtm-initialized])').forEach((nav) => {
      nav.dataset.mtmInitialized = 'true';
      const links = [...nav.querySelectorAll('a[href^="#"]')];
      const sections = links
        .map((link) => document.querySelector(link.getAttribute('href')))
        .filter(Boolean);

      if (!('IntersectionObserver' in window) || sections.length === 0) return;
      const observer = new IntersectionObserver((entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!visible) return;

        links.forEach((link) => {
          const active = link.getAttribute('href') === `#${visible.target.id}`;
          link.classList.toggle('is-active', active);
          if (active) {
            link.setAttribute('aria-current', 'location');
            link.scrollIntoView({ behavior: reducedMotionQuery.matches ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
          } else {
            link.removeAttribute('aria-current');
          }
        });
      }, { rootMargin: '-25% 0px -60% 0px', threshold: [0, 0.1, 0.25] });

      sections.forEach((section) => observer.observe(section));
    });
  };

  const synchronizeProcessSteps = (scope = document) => {
    scope.querySelectorAll('.mtm-process__steps').forEach((container) => {
      const steps = [...container.querySelectorAll('[data-process-step]')];
      if (mobileProcessQuery.matches) {
        steps.forEach((step, index) => {
          if (!step.dataset.mtmMobileProcess) {
            step.open = index === 0;
            step.dataset.mtmMobileProcess = 'true';
          }
        });
      } else {
        steps.forEach((step) => {
          step.open = true;
          delete step.dataset.mtmMobileProcess;
        });
      }
    });
  };

  const initialize = (scope = document) => {
    initializeLightboxes(scope);
    initializeProjectFilters(scope);
    initializeSeriesNavigation(scope);
    synchronizeProcessSteps(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  mobileProcessQuery.addEventListener?.('change', () => synchronizeProcessSteps());
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
